import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Download, Copy, Loader2,
  CheckCircle, Clock, User, MessageSquare, Zap, ChevronDown, ChevronUp,
  Check, Circle, BookOpen
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function MeetingReview() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [copied, setCopied] = useState(false);
  const autoGeneratedRef = useRef(false);
  const [sourceUsage, setSourceUsage] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${API}/meetings/${eventId}/review`, { withCredentials: true });
        setReviewData(resp.data);
        const { review, transcript, notes } = resp.data || {};
        const hasData = (transcript?.length > 0) || (notes?.length > 0);
        if (!review && hasData && !autoGeneratedRef.current) {
          autoGeneratedRef.current = true;
          setGenerating(true);
          (async () => {
            try {
              await axios.post(`${API}/meetings/${eventId}/summary`, {}, { withCredentials: true });
              const reviewResp = await axios.get(`${API}/meetings/${eventId}/review`, { withCredentials: true });
              setReviewData(reviewResp.data);
              toast.success("Summary generated");
            } catch (err) {
              toast.error("Failed to generate summary");
              autoGeneratedRef.current = false;
            } finally {
              setGenerating(false);
            }
          })();
        }
      } catch (err) {
        toast.error("Failed to load review data");
      } finally {
        setLoading(false);
      }
    };
    load();
    axios.get(`${API}/meetings/${eventId}/source-usage`, { withCredentials: true })
      .then(r => setSourceUsage(r.data || []))
      .catch(() => {});
  }, [eventId]);

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const resp = await axios.post(
        `${API}/meetings/${eventId}/summary`,
        {},
        { withCredentials: true }
      );
      const reviewResp = await axios.get(`${API}/meetings/${eventId}/review`, { withCredentials: true });
      setReviewData(reviewResp.data);
      toast.success("Summary generated");
    } catch (err) {
      toast.error("Failed to generate summary");
    } finally {
      setGenerating(false);
    }
  };

  const generateMarkdown = () => {
    if (!reviewData) return "";
    const { event, agenda, items, notes, review, coverage } = reviewData;
    const covByItemId = Object.fromEntries(
      (coverage?.items || []).map((c) => [c.item_id, c])
    );
    let md = `# ${event?.title || 'Meeting'} - Summary\n\n`;
    if (review?.summary) {
      md += `## Summary\n${review.summary}\n\n`;
    }
    if (items?.length > 0) {
      md += `## Agenda & Notes\n\n`;
      items.forEach((item, idx) => {
        md += `### ${idx + 1}. ${item.title}\n`;
        md += `*${item.intent}* (${item.time_alloc_mins} min)\n\n`;
        const cov = covByItemId[item.item_id];
        const covNotes = cov?.notes || [];
        const dbNotes = (notes || []).filter(n => n.agenda_item_id === item.item_id);
        const itemNotes = covNotes.length > 0 ? covNotes : dbNotes.map((n) => n.text);
        if (Array.isArray(itemNotes) && itemNotes.length > 0) {
          itemNotes.forEach((n) => { md += `- ${n}\n`; });
          md += '\n';
        }
        if (item.sub_points?.length > 0) {
          item.sub_points.forEach((sp) => { md += `  - ${sp}\n`; });
          md += '\n';
        }
      });
    }
    if (review?.action_items?.length > 0) {
      md += `## Action Items\n\n`;
      review.action_items.forEach(ai => {
        md += `- [ ] ${ai.task}`;
        if (ai.owner) md += ` (@${ai.owner})`;
        if (ai.due_date) md += ` [Due: ${ai.due_date}]`;
        md += '\n';
      });
      md += '\n';
    }
    if (review?.key_decisions?.length > 0) {
      md += `## Key Decisions\n\n`;
      review.key_decisions.forEach(kd => { md += `- ${kd}\n`; });
    }
    return md;
  };

  const copyMarkdown = async () => {
    const md = generateMarkdown();
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      toast.success("Copied to clipboard");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = md;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success("Copied to clipboard");
    }
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadMarkdown = () => {
    const md = generateMarkdown();
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reviewData?.event?.title || 'meeting'}-summary.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  const getNotesForItem = (itemId) => (reviewData?.notes || []).filter(n => n.agenda_item_id === itemId);

  if (loading) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-border border-t-foreground rounded-full animate-spin" />
      </div>
    );
  }

  const { event, agenda, items, notes, review, transcript, coverage } = reviewData || {};
  const covByItemId = Object.fromEntries(
    (coverage?.items || []).map((c) => [c.item_id, c])
  );

  return (
    <div className="min-h-screen mesh-bg">
      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 surface-nav">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-dashboard-review"
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="rounded-full h-8 w-8 p-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/dashboard")}>
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-heading text-xs">S</span>
              </div>
              <span className="font-heading text-foreground text-base">Shadow</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="copy-md-btn"
              onClick={copyMarkdown}
              variant="outline"
              className="rounded-full h-8 gap-1.5 text-xs"
            >
              {copied ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              data-testid="download-md-btn"
              onClick={downloadMarkdown}
              className="rounded-full h-8 gap-1.5 text-xs px-3.5"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <p className="text-muted-foreground text-xs tracking-widest uppercase font-medium mb-2">Meeting Review</p>
            <h1 className="font-heading text-2xl sm:text-[28px] text-foreground tracking-tight mb-1.5">
              {event?.title || "Meeting"}
            </h1>
            {event?.description && (
              <p className="text-muted-foreground text-sm leading-relaxed">{event.description}</p>
            )}
          </motion.div>

          {/* Executive Summary */}
          {review?.summary && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="surface-card rounded-2xl p-8 md:p-10 mb-6"
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-xl bg-primary/5 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-primary" />
                </div>
                <h2 className="font-heading text-foreground text-base">Summary</h2>
              </div>
              <p className="text-foreground/80 text-sm leading-[1.7] whitespace-pre-line">{review.summary}</p>
            </motion.div>
          )}

          {/* Action Items */}
          {review?.action_items?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className="surface-card rounded-2xl p-8 md:p-10 mb-6"
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/5 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <h2 className="font-heading text-foreground text-base">Action Items</h2>
                <Badge variant="outline" className="text-[10px] ml-auto">
                  {review.action_items.length}
                </Badge>
              </div>
              <div className="space-y-2.5">
                {review.action_items.map((ai, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 bg-secondary/50 rounded-xl p-4 border border-border"
                    data-testid={`action-item-${idx}`}
                  >
                    <div className="w-4 h-4 rounded border-2 border-border shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground font-medium">{ai.task}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        {ai.owner && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <User className="w-2.5 h-2.5" /> {ai.owner}
                          </span>
                        )}
                        {ai.due_date && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
                            <Clock className="w-2.5 h-2.5" /> {ai.due_date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Key Decisions */}
          {review?.key_decisions?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24 }}
              className="surface-card rounded-2xl p-8 md:p-10 mb-6"
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-xl bg-amber-500/5 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-amber-600" />
                </div>
                <h2 className="font-heading text-foreground text-base">Key Decisions</h2>
              </div>
              <ul className="space-y-2.5">
                {review.key_decisions.map((kd, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                    <p className="text-sm text-foreground/80 leading-relaxed">{kd}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Agenda Notes */}
          {items?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              className="surface-card rounded-2xl p-8 md:p-10 mb-6"
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-xl bg-primary/5 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-primary" />
                </div>
                <h2 className="font-heading text-foreground text-base">Agenda Notes</h2>
              </div>
              <div className="space-y-5">
                {items.map((item, idx) => {
                  const cov = covByItemId[item.item_id];
                  const dbNotes = getNotesForItem(item.item_id);
                  const covNotes = cov?.notes || [];
                  const itemNotes = covNotes.length > 0
                    ? covNotes.map((t, i) => ({ note_id: `cov-${idx}-${i}`, text: t }))
                    : dbNotes;
                  const subPoints = item.sub_points || [];
                  const covSubPoints = cov?.sub_points || [];
                  return (
                    <div key={item.item_id} data-testid={`review-agenda-${item.item_id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={cov?.covered ? "success" : "secondary"} className="text-[10px]">
                          {cov?.covered ? "Covered" : "Not covered"}
                        </Badge>
                        <span className="font-mono text-[10px] text-muted-foreground">{String(idx + 1).padStart(2, '0')}</span>
                        <h4 className="font-medium text-foreground text-sm">{item.title}</h4>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {item.time_alloc_mins}m
                        </Badge>
                      </div>
                      {subPoints.length > 0 && (
                        <div className="ml-6 space-y-1 mb-2">
                          {subPoints.map((sp, spi) => {
                            const spCovered = covSubPoints[spi]?.covered ?? false;
                            return (
                              <div key={spi} className="flex items-center gap-2">
                                {spCovered ? (
                                  <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                                ) : (
                                  <Circle className="w-3 h-3 text-muted-foreground/30 shrink-0" />
                                )}
                                <p className="text-xs text-foreground/70">{sp}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {itemNotes.length > 0 ? (
                        <div className="ml-6 space-y-1.5">
                          {itemNotes.map(n => (
                            <div key={n.note_id} className="flex items-start gap-2">
                              <div className="w-1 h-1 rounded-full bg-primary/25 mt-1.5 shrink-0" />
                              <p className="text-xs text-muted-foreground leading-relaxed">{n.text}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="ml-6 text-[10px] text-muted-foreground/40">No notes captured</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Generate Summary (if no review exists) */}
          {!review && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="surface-card rounded-2xl p-10 mb-6 text-center"
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-5">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-foreground text-lg mb-2">Generate Summary</h3>
              <p className="text-muted-foreground text-sm mb-8 leading-relaxed max-w-sm mx-auto">
                AI will analyze the transcript and notes to create a structured summary with action items.
              </p>
              <Button
                data-testid="generate-summary-btn"
                onClick={generateSummary}
                disabled={generating}
                className="rounded-full h-9 gap-2 text-sm px-6"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {generating ? "Generating..." : "Generate Summary"}
              </Button>
            </motion.div>
          )}

          {/* Sources Used */}
          {sourceUsage.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="surface-card rounded-2xl p-8 mb-6"
            >
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-7 h-7 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <BookOpen className="w-3.5 h-3.5 text-teal-600" />
                </div>
                <h3 className="font-heading text-foreground text-sm">Sources Used</h3>
                <span className="text-[10px] text-muted-foreground font-mono">
                  {new Set(sourceUsage.map(u => u.source_id)).size} source{new Set(sourceUsage.map(u => u.source_id)).size !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="space-y-3">
                {sourceUsage.map((usage, i) => (
                  <div key={usage.usage_id || i} className="flex items-start gap-3 bg-secondary/50 rounded-lg p-3">
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-teal-500/10 text-teal-700 font-medium shrink-0 mt-0.5">
                      {usage.usage_type === "fact_grounding" ? "Fact" : usage.usage_type === "objection" ? "Objection" : "Reinforcement"}
                    </span>
                    <div className="flex-1 min-w-0">
                      {usage.transcript_snippet && (
                        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                          "{usage.transcript_snippet}"
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground/60 mt-1">
                        {usage.retrieval_layer === "deep" ? "Deep retrieval" : "Summary-based"} · confidence {Math.round((usage.confidence || 0) * 100)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Full Transcript */}
          {transcript?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="surface-card rounded-2xl overflow-hidden mb-6"
            >
              <button
                data-testid="toggle-transcript"
                onClick={() => setShowTranscript(!showTranscript)}
                className="w-full px-8 py-5 flex items-center justify-between hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-foreground text-sm">Transcript</h3>
                  <span className="text-[10px] text-muted-foreground font-mono">{transcript.length} segments</span>
                </div>
                {showTranscript ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              <AnimatePresence>
                {showTranscript && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-8 space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                      {transcript.map(seg => (
                        <div key={seg.segment_id} className="flex items-start gap-3">
                          <span className="text-[10px] font-medium text-primary/50 w-14 shrink-0 font-mono pt-0.5">
                            {seg.timestamp ? new Date(seg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                          </span>
                          <div>
                            <span className="text-[10px] font-medium text-muted-foreground">{seg.speaker}</span>
                            <p className="text-xs text-foreground/70 leading-relaxed">{seg.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Regenerate button */}
          <div className="text-center pt-4">
            <Button
              data-testid="regenerate-summary-btn"
              onClick={generateSummary}
              disabled={generating}
              variant="ghost"
              className="rounded-full h-8 gap-1.5 text-xs text-muted-foreground"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              {generating ? "Regenerating..." : "Regenerate Summary"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
